import { useMutation, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '@/lib/queryKeys'

import { createAddress } from '../addressService'
import type { Address, CreateAddressPayload } from '../types/Address'

export const useCreateAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAddressPayload) => createAddress(payload),
    onMutate: async newAddressData => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })

      // Snapshot the previous value
      const previousAddresses = queryClient.getQueryData<Address[]>([QUERY_KEYS.ADDRESSES])

      // Optimistically update to the new value
      const tempAddressId = `temp-${Date.now()}`
      const optimisticAddress: Address = {
        id: tempAddressId,
        userId: '', // Will be set by server
        street: newAddressData.street,
        apartment: newAddressData.apartment || '',
        city: newAddressData.city,
        postalCode: newAddressData.postalCode,
        note: newAddressData.note || '',
        isDefault: previousAddresses?.length === 0, // First address is default
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES], old => {
        if (!old) return [optimisticAddress]
        return [...old, optimisticAddress]
      })

      // Return a context object with the snapshotted value
      return { previousAddresses, tempAddressId }
    },
    onError: (_error, _newAddress, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousAddresses && context?.tempAddressId) {
        queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES], old => old?.filter(addr => addr.id !== context.tempAddressId))
      }
    },
    onSuccess: newAddress => {
      // Replace optimistic address with real one from server
      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES], old =>
        old?.map(addr => (addr.id.startsWith('temp-') ? newAddress : addr))
      )
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })
    },
  })
}
